import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import Styles from './UserSignupHeatmap.module.css'
import { Colors } from 'Theme'
import PropTypes from 'prop-types'
import AnalyticsSelectors from 'Stores/Analytics/Selectors'
import AnalyticsActions from 'Stores/Analytics/Actions'
import ChartComponent from 'Components/ChartComponent/ChartComponent'
import RequestState from 'Enums/RequestState'
import IFSkeleton from 'Components/IFSkeleton/IFSkeleton'
import { useApexResizeObserver } from 'Utils/Resizeobserver'
import IFText from 'Components/IFText/IFText'
import { useTranslation } from 'react-i18next'

const UserSignupHeatmap = ({
  fetchUserSignup,
  userSignup,
  filter,
  fetchUserSignupRequestState,
}) => {
  const { t } = useTranslation()

  const chartWrapperRef = useApexResizeObserver()
  useEffect(() => {
    fetchUserSignup(filter)
  }, [filter])

  const heatmapData = userSignup?.heatmap || {}

  const years = Object.keys(heatmapData)
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]

  const series = years.map((year) => ({
    name: year,
    data: months.map((month, monthIndex) => {
      const value = heatmapData[year][monthIndex]
      return {
        x: month,
        y: value === 0 ? 0.01 : value ?? 0,
      }
    }),
  }))

  const allValues = Object.values(heatmapData)
    .flat()
    .filter((v) => typeof v === 'number')

  const minValue = allValues.length ? Math.min(...allValues) : 0
  const maxValue = allValues.length ? Math.max(...allValues) : 0
  const allZero = allValues.every((v) => v === 0)
  const offsetValue = maxValue / 2

  const defaultRanges = [
    {
      from: 81,
      to: 100,
      name: '100+',
      color: Colors.Analytics.PeakHour.Shade1,
    },
    { from: 61, to: 80, name: '≥ 61', color: Colors.Analytics.PeakHour.Shade2 },
    { from: 41, to: 60, name: '≥ 41', color: Colors.Analytics.PeakHour.Shade3 },
    { from: 21, to: 40, name: '≥ 21', color: Colors.Analytics.PeakHour.Shade4 },
    { from: 11, to: 20, name: '≥ 11', color: Colors.Analytics.PeakHour.Shade5 },
    { from: 0, to: 10, name: '≥ 0', color: Colors.Analytics.PeakHour.Shade7 },
  ]

  const ranges = !allZero
    ? Array.from({ length: 5 }, (_, i) => {
        const step = (maxValue - minValue) / 5
        const rangeBottom = Math.max(minValue + i * step).toFixed(0)
        const rangeTop =
          i === 4
            ? Math.max(maxValue).toFixed(0)
            : Math.max(minValue + (i + 1) * step).toFixed(0)

        return {
          from: rangeBottom,
          to: rangeTop,
          name: `≥ ${rangeBottom}`,
          color: [
            Colors.Analytics.PeakHour.Shade7,
            Colors.Analytics.PeakHour.Shade5,
            Colors.Analytics.PeakHour.Shade4,
            Colors.Analytics.PeakHour.Shade3,
            Colors.Analytics.PeakHour.Shade2,
            Colors.Analytics.PeakHour.Shade1,
          ][i],
        }
      }).reverse()
    : defaultRanges

  const options = {
    chart: {
      type: 'heatmap',
      toolbar: { show: false },
      animations: {
        enabled: true,
        speed: 500,
        animateGradually: { enabled: true, delay: 50 },
        dynamicAnimation: { enabled: true, speed: 500 },
      },
    },

    tooltip: {
      y: {
        formatter: (value) => {
          if (value === 0.01) return 0
          return Math.round(value)
        },
      },
    },

    plotOptions: {
      heatmap: {
        shadeIntensity: 0,
        colorScale: { ranges },
        radius: 0,
        padding: 0,
        enableShades: true,
        distributed: false,
      },
    },
    fill: {
      opacity: 1,
    },
    dataLabels: {
      enabled: true,
      style: {
        fontWeight: 400,
        colors: [
          function ({ seriesIndex, dataPointIndex, w }) {
            if (
              w.config.series[seriesIndex].data[dataPointIndex].y > offsetValue
            ) {
              return '#fff'
            } else {
              return '#000'
            }
          },
        ],
      },
      formatter: function (val) {
        return Math.max(val).toFixed(0)
      },
      background: {
        enabled: false,
      },
    },
    xaxis: { categories: months, labels: { rotate: -45 } },
    yaxis: { labels: { style: { fontSize: '12px' } } },
    legend: { position: 'bottom' },
  }
  return (
    <div style={{ backgroundColor: Colors.white }} className={Styles.Card}>
      {fetchUserSignupRequestState === RequestState.LOADING ? (
        <IFSkeleton
          className={Styles.ChartSkeleton}
          variant="rectangular"
          height={'100%'}
          width={'100%'}
        />
      ) : (
        <div ref={chartWrapperRef} className={Styles.Chart}>
          <ChartComponent
            type="heatmap"
            options={options}
            series={series}
            height="100%"
            width="100%"
            className={
              !(
                fetchUserSignupRequestState === RequestState.LOADING ||
                fetchUserSignupRequestState === RequestState.SUCCEEDED ||
                fetchUserSignupRequestState == RequestState.UNINITIALIZED
              ) && Styles.OverlayTest
            }
          />

          {!(
            fetchUserSignupRequestState === RequestState.LOADING ||
            fetchUserSignupRequestState === RequestState.SUCCEEDED ||
            fetchUserSignupRequestState == RequestState.UNINITIALIZED
          ) && (
            <IFText
              className={Styles.OverlayText}
              style={{ color: Colors.red }}
            >
              {t('Analytics.NoData')}
            </IFText>
          )}
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  userSignup: AnalyticsSelectors.getUserSignup(state),
  fetchUserSignupRequestState:
    AnalyticsSelectors.getFetchUserSignupRequestState(state),
})

const mapDispatchToProps = (dispatch) => ({
  fetchUserSignup: (filter) =>
    dispatch(AnalyticsActions.fetchUserSignup(filter)),
})

UserSignupHeatmap.propTypes = {
  filter: PropTypes.object,
  userSignup: PropTypes.object,
  fetchUserSignup: PropTypes.func,
  fetchUserSignupRequestState: PropTypes.number,
}

export default connect(mapStateToProps, mapDispatchToProps)(UserSignupHeatmap)
